import { styled, alpha } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import { DRAWER_WIDTH } from "../../constants";

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  position: "fixed",
  height: "100vh",
  zIndex: theme.zIndex.drawer,
  "& .MuiDrawer-paper": {
    position: "fixed",
    top: "64px",
    height: "calc(100% - 64px)",
    width: DRAWER_WIDTH,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(8),
      },
    }),
  },
}));

export const SidebarContainer = styled("div")`
  display: flex;
  position: fixed;
  top: 48px;
  left: 0;
  bottom: 0;
  z-index: 1200;
  transition: all 0.3s ease;
  height: calc(100vh - 48px);
`;

export const PrimarySidebar = styled(Box)(({ theme }) => ({
  width: "45px",
  height: "100%",
  backgroundColor: "rgb(44, 52, 123)",
  display: "flex",
  flexDirection: "column",
  padding: "16px 0",
  borderRight: `1px solid #e0e0e0`,
}));

export const SecondarySidebar = styled(Box, {
  shouldForwardProp: (prop) => !["collapsed", "hidden"].includes(prop),
})(({ theme, collapsed, hidden }) => ({
  width: collapsed || hidden ? 0 : "260px",
  height: "100%",
  maxHeight: "100%",
  backgroundColor: "#ffffff",
  opacity: collapsed || hidden ? 0 : 1,
  transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease",
  display: hidden ? "none" : "flex",
  flexDirection: "column",
  borderRight: "1px solid #E5E7EB",
  boxShadow: collapsed || hidden ? "none" : "2px 0 8px rgba(0, 0, 0, 0.04)",
  overflow: "hidden",
  boxSizing: "border-box",
}));

export const ScrollableContent = styled("div")({
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  padding: "4px 2px",
  background: "#ffffff",
  boxSizing: "border-box",
  width: "100%",
  maxWidth: "100%",

  "&::-webkit-scrollbar": {
    width: 6,
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f5f9",
    borderRadius: 3,
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#cbd5e1",
    borderRadius: 3,
    "&:hover": {
      background: "#94a3b8",
    },
  },
  scrollbarWidth: "thin",
  msOverflowStyle: "auto",
});
export const MenuSection = styled(Box)({
  "& + &": {
    marginTop: "8px",
  },

  "&:last-child": {
    paddingBottom: "16px",
  },
});

export const MenuSubSection = styled(Box)({
  "& + &": {
    marginTop: "8px",
  },

  "&:last-child": {
    paddingBottom: "36px",
    marginLeft: "8px",
  },
});

export const PrimaryMenuItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  width: "30px",
  height: "30px",
  display: "flex",
  borderRadius: "8px",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  backgroundColor: active ? "#ffffff" : "transparent",
  color: active ? "rgb(44, 52, 123)" : "#fff",
  margin: "0 auto",
  "& .MuiSvgIcon-root": {
    fontSize: "20px",
    color: active ? "rgb(44, 52, 123)" : "#fff",
  },
  "&:hover": {
    backgroundColor: active ? "#ffffff" : "rgba(44, 52, 123, 0.08)",
    color: active ? "rgb(44, 52, 123)" : "#fff",
  },
}));

export const MenuItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => {
  return {
    padding: "6px 12px",
    margin: "2px 0",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: active ? "rgba(44, 52, 123, 0.12)" : "transparent",
    color: "rgb(44, 52, 123)",
    height: "36px",
    transition: "all 0.2s ease",
    "& .MuiSvgIcon-root": {
      fontSize: "18px",
      color: "rgb(44, 52, 123)",
      transition: "all 0.2s ease",
    },
    "& span": {
      fontSize: "12px",
      fontWeight: 500,
      letterSpacing: "0.1px",
      transition: "all 0.2s ease",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "&:hover": {
      backgroundColor: "rgba(44, 52, 123, 0.08)",
      color: "rgb(44, 52, 123)",
      transform: "translateX(2px)",
    },
  };
});

export const MenuBadge = styled(Box)(({ theme }) => ({
  padding: "1px 6px",
  borderRadius: "3px",
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.default, 0.4)
      : alpha(theme.palette.primary.main, 0.08),
  color:
    theme.palette.mode === "dark"
      ? theme.palette.primary.light
      : theme.palette.primary.light,
  fontSize: "10px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  height: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "28px",
}));

export const CollapseButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "collapsed",
})(({ theme, collapsed }) => ({
  width: "32px",
  height: "32px",
  border: "none",
  backgroundColor: theme.sidebar.active,
  borderRadius: "10px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  padding: 0,
  color: "#fff",
  transition: "all 0.2s ease",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",

  "& .MuiSvgIcon-root": {
    width: 18,
    height: 18,
    transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
    transition: "all 0.3s ease",
  },

  "&:hover": {
    color: theme.sidebar.text.primary,
    backgroundColor: theme.sidebar.hover,
    transform: "translateY(-1px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
  },

  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
}));

export const ProfileSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})(({ theme, collapsed }) => ({
  marginTop: "auto",
  padding: "12px",
  borderTop: `1px solid ${theme.sidebar.divider}`,
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minHeight: "68px",
  backgroundColor: theme.sidebar.primary,
  position: "sticky",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1,

  "& .MuiAvatar-root": {
    width: "32px",
    height: "32px",
  },
}));

export const ProfileAvatar = styled("div")(({ theme }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  overflow: "hidden",
  flexShrink: 0,
  border: `1px solid ${theme.palette.divider}`,

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

export const MenuTitle = styled(Typography)(({ theme }) => {
  const colors = theme.palette.mode === "dark" ? theme.sidebar : theme.sidebar;

  return {
    color: colors.text.secondary,
    fontSize: "10px",
    fontWeight: 500,
    textTransform: "uppercase",
    padding: "0 12px",
    marginBottom: "4px",
    marginTop: "20px",
    letterSpacing: "0.5px",
    opacity: 0.9,
  };
});

export const ProfileCard = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: "92px",
  left: "52px",
  backgroundColor: "rgb(35, 42, 100)",
  borderRadius: "8px",
  padding: "12px 14px",
  minWidth: "180px",
  maxWidth: "240px",
  width: "fit-content",
  border: "1px solid rgba(44, 52, 123, 0.6)",
  display: "none",
  boxShadow: "0 8px 24px rgba(0,0,0,0.28)",

  "& p, & span": {
    overflow: "visible",
    textOverflow: "clip",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },

  "&.show": {
    display: "block",
    animation: "fadeIn 0.2s ease-out",
  },
  zIndex: 1300,
  "&:before": {
    content: '""',
    position: "absolute",
    left: "-4px",
    top: "50%",
    transform: "translateY(-50%) rotate(45deg)",
    width: "8px",
    height: "8px",
    backgroundColor: "rgb(35, 42, 100)",
    border: "1px solid rgba(44, 52, 123, 0.6)",
    borderRight: "none",
    borderTop: "none",
  },
}));

export const ProfileCardContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
}));

export const ProfileName = styled(Typography)(({ theme }) => ({
  color: theme.sidebar.text.primary,
  fontSize: "14px",
  fontWeight: 600,
  marginBottom: "4px",
}));

export const ProfileSectionName = styled(Typography)(({ theme }) => ({
  color: theme.sidebar.text.secondary,
  fontSize: "12px",
  fontWeight: 500,
}));

export const ProfileRole = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 500,
  color: "#B8BCE3",
  letterSpacing: "0.2px",
  lineHeight: 1.2,
}));

export const DraggableTreeItem = styled(Box)(({ theme }) => ({
  padding: "4px 8px",
  margin: "2px 0",
  borderRadius: "4px",
  cursor: "move",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.05),
  },
}));

export const TreeItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "draggable",
})(({ theme }) => ({
  width: "100%",
  maxWidth: "100%",
  margin: "2px 0",
  boxSizing: "border-box",
  overflow: "hidden",
}));

export const TreeCardContainer = styled(Box)(({ theme }) => ({
  background: "rgba(255,255,255,0.75)",
  border: "1.5px solid",
  borderImage: "linear-gradient(120deg, #e0e7ef 60%, #c7d2fe 100%) 1",
  borderRadius: "18px",
  boxShadow: "0 8px 32px rgba(30,34,90,0.10)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  position: "relative",
}));

export const TreeSectionHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  marginBottom: 12,
});

export const TreeSectionAccent = styled("div")({
  width: 4,
  height: 24,
  borderRadius: 2,
  marginRight: 10,
  background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
});

export const TreeSectionTitle = styled("span")({
  color: "#4B5563",
  fontWeight: 800,
  fontSize: "1.08rem",
  letterSpacing: 1.2,
  textTransform: "uppercase",
});

const LIGHT_GRAY_06 = "rgba(75,85,99,0.6)";

export const TreeItemContent = styled(Box, {
  shouldForwardProp: (prop) => !['iscolumn', 'istable', 'selected', 'level'].includes(prop),
})(
  ({ theme, selected, level, iscolumn, istable }) => ({
    display: "flex",
    alignItems: "center",
    gap: iscolumn ? "5px" : istable ? "8px" : "6px",
    padding: iscolumn 
      ? "2px 6px" 
      : istable 
      ? "4px 8px" 
      : "2px 6px",
    borderRadius: iscolumn ? "4px" : istable ? "5px" : "4px",
    marginBottom: iscolumn ? "0.5px" : istable ? "2px" : "0.5px",
    background: selected
      ? "#E0E7FF"
      : iscolumn
      ? "#F8F9FA"
      : istable
      ? "#FFFFFF"
      : "transparent",
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    border: selected
      ? "1px solid #C7D2FE"
      : istable
      ? "1px solid #E5E7EB"
      : "1px solid transparent",
    color: iscolumn ? "#6B7280" : istable ? "#374151" : LIGHT_GRAY_06,
    fontWeight: iscolumn ? 400 : istable ? 500 : selected ? 500 : 400,
    fontSize: iscolumn ? "10.5px" : istable ? "11.5px" : level === 1 ? "11px" : "11px",
    transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: iscolumn ? "grab" : "pointer",
    position: "relative",
    boxShadow: selected 
      ? "0 1px 2px rgba(44, 52, 123, 0.08)" 
      : istable
      ? "0 1px 2px rgba(0, 0, 0, 0.03)"
      : "none",
    "&:hover": {
      background: selected
        ? "#C7D2FE"
        : iscolumn
        ? "#F3F4F6"
        : istable
        ? "#F8F9FA"
        : "#F8F9FA",
      color: iscolumn ? "#4B5563" : istable ? "#1F2937" : LIGHT_GRAY_06,
      boxShadow: istable ? "0 2px 4px rgba(0, 0, 0, 0.06)" : "none",
      borderColor: istable ? "#D1D5DB" : "transparent",
    },
    "&:active": {
      transform: iscolumn ? "scale(0.98)" : istable ? "scale(0.99)" : "none",
    },
  })
);

export const TreeItemLabel = styled(Typography, {
  shouldForwardProp: (prop) => !['iscolumn', 'istable'].includes(prop),
})(({ theme, iscolumn, istable }) => ({
  fontSize: iscolumn ? "10.5px" : istable ? "11.5px" : "11px",
  color: iscolumn ? "#6B7280" : istable ? "#374151" : LIGHT_GRAY_06,
  fontWeight: "inherit",
  flex: "1 1 auto",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
  maxWidth: "100%",
  display: "flex",
  alignItems: "center",
  gap: iscolumn ? "4px" : istable ? "6px" : "5px",
  boxSizing: "border-box",
  "& span": {
    fontSize: "10px",
    color: "#9CA3AF",
    fontWeight: 400,
    flexShrink: 0,
  },
}));

export const TreeItemIcon = styled(Box, {
  shouldForwardProp: (prop) => !['iscolumn', 'istable'].includes(prop),
})(({ theme, iscolumn, istable }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  color: iscolumn ? "#9CA3AF" : istable ? "#6B7280" : LIGHT_GRAY_06,
  minWidth: iscolumn ? "14px" : istable ? "20px" : "16px",
  flexShrink: 0,
  gap: "5px",
  "& .MuiSvgIcon-root": {
    fontSize: iscolumn ? "13px" : istable ? "15px" : "14px",
    opacity: 1,
    color: iscolumn ? "#9CA3AF" : istable ? "#6B7280" : LIGHT_GRAY_06,
    background: "none",
    WebkitBackgroundClip: "unset",
    WebkitTextFillColor: "unset",
    transition: "color 0.15s ease",
  },
}));

export const TypeBadge = styled(Box)(({ theme, type }) => {
  let bg = "rgba(243,244,246,0.7)",
    color = "#6b7280",
    border = "#e0e7ef";
  if (type === "string") {
    bg = "rgba(224,242,254,0.7)";
    color = "#0284c7";
    border = "#38bdf8";
  } else if (type === "int") {
    bg = "rgba(254,249,195,0.7)";
    color = "#b45309";
    border = "#fde047";
  } else if (type === "date") {
    bg = "rgba(237,233,254,0.7)";
    color = "#7c3aed";
    border = "#a78bfa";
  } else if (type === "boolean") {
    bg = "rgba(220,252,231,0.7)";
    color = "#16a34a";
    border = "#4ade80";
  }
  return {
    padding: "3px 14px",
    borderRadius: "16px",
    backgroundColor: bg,
    color: color,
    fontSize: "0.93rem",
    fontWeight: 700,
    marginLeft: 10,
    whiteSpace: "nowrap",
    letterSpacing: "0.3px",
    border: `1.5px solid ${border}`,
    minWidth: "36px",
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(30,34,90,0.07)",
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
  };
});

export const TableHeader = styled(Box)(({ theme }) => ({
  padding: "3px 8px 1px 8px",
  backgroundColor: "transparent",
  borderBottom: "none",
  marginBottom: "1px",
  marginTop: "1px",
}));

export const TableTitle = styled(Typography)(({ theme }) => ({
  fontSize: "11px",
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginLeft: "0px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
}));

export const ColumnList = styled(Box)({
  padding: "0 2px",
  width: "100%",
  maxWidth: "100%",
  marginTop: "2px",
  boxSizing: "border-box",
  overflow: "hidden",
});

export const ColumnPlaceholder = styled(Box)(({ theme }) => ({
  width: "40px",
  height: "16px",
  borderRadius: "2px",
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
  marginLeft: "auto",
  flexShrink: 0,
}));
